import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, UserPlus, ClipboardCheck, Search, Target,
  CalendarClock, User, Building2, Bell, LogOut, Menu, ChevronsUpDown, ShieldCheck, Wallet, Clock, MailWarning, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { UserAvatar } from "@/components/ui-bits";
import { roleHome } from "@/lib/constants";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { fmtDate, fmtTime } from "@/lib/constants";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const NAV = {
  hr: [
    { to: "/hr", labelKey: "nav.overview", testid: "nav-overview", icon: LayoutDashboard, end: true },
    { to: "/hr/network", labelKey: "nav.expertNetwork", testid: "nav-expert-network", icon: Users, badgeKey: "network" },
    { to: "/hr/employees", labelKey: "nav.employees", testid: "nav-employees", icon: UserPlus },
    { to: "/hr/approvals", labelKey: "nav.approvals", testid: "nav-approvals", icon: ClipboardCheck, badgeKey: "approvals" },
    { to: "/hr/rates", labelKey: "nav.rates", testid: "nav-rates", icon: Wallet },
  ],
  employee: [
    { to: "/app", labelKey: "nav.overview", testid: "nav-overview", icon: LayoutDashboard, end: true },
    { to: "/experts", labelKey: "nav.findExpert", testid: "nav-find-an-expert", icon: Search },
    { to: "/app/goals", labelKey: "nav.myGoals", testid: "nav-my-learning-goals", icon: Target },
  ],
  expert: [
    { to: "/expert", labelKey: "nav.overview", testid: "nav-overview", icon: LayoutDashboard, end: true },
    { to: "/expert/networks", labelKey: "nav.networks", testid: "nav-networks", icon: Building2, badgeKey: "networks" },
    { to: "/expert/availability", labelKey: "nav.availability", testid: "nav-availability", icon: CalendarClock },
    { to: "/expert/profile", labelKey: "nav.myProfile", testid: "nav-my-profile", icon: User },
  ],
  expertPending: [
    { to: "/expert/pending", labelKey: "nav.applicationStatus", testid: "nav-application-status", icon: Clock, end: true },
    { to: "/expert/profile", labelKey: "nav.myProfile", testid: "nav-my-profile", icon: User },
  ],
  admin: [
    { to: "/admin", labelKey: "nav.overview", testid: "nav-overview", icon: LayoutDashboard, end: true },
  ],
};

const ROLE_LABEL_KEY = { hr: "nav.roleHr", employee: "nav.roleMember", expert: "nav.roleExpert", admin: "nav.roleAdmin" };

function NavItems({ items, badges, onNavigate }) {
  const { t } = useTranslation();
  return (
    <nav className="space-y-0.5">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          data-testid={item.testid}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive ? "bg-card text-foreground shadow-sm border border-border" : "text-muted-foreground hover:text-foreground hover:bg-card/60"
            }`
          }
        >
          <item.icon size={18} strokeWidth={1.75} />
          <span className="flex-1">{t(item.labelKey)}</span>
          {item.badgeKey && badges[item.badgeKey] > 0 && (
            <span className="min-w-5 h-5 px-1.5 grid place-items-center rounded-full bg-primary text-primary-foreground text-[11px] font-semibold tabular">
              {badges[item.badgeKey]}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function NotificationBell() {
  const { t } = useTranslation();
  const [notifs, setNotifs] = useState([]);
  const load = () => api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const unread = notifs.filter((n) => !n.read).length;
  const markRead = async () => { await api.post("/notifications/read-all").catch(() => {}); load(); };

  return (
    <DropdownMenu onOpenChange={(o) => o && unread && markRead()}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell" className="relative grid place-items-center w-9 h-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
          <Bell size={18} strokeWidth={1.75} />
          {unread > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary ring-2 ring-card" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b border-border">
          <p className="text-sm font-semibold">{t("nav.notifications")}</p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">{t("nav.allCaughtUp")}</p>
          ) : (
            notifs.slice(0, 12).map((n) => (
              <div key={n.notification_id} className="px-4 py-3 border-b border-border/60 last:border-0 hover:bg-secondary/50">
                <p className="text-sm font-medium text-foreground">{n.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{n.body}</p>
                <p className="text-[11px] text-muted-foreground/70 mt-1">{fmtDate(n.created_at)} · {fmtTime(n.created_at)}</p>
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function VerifyEmailBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  if (dismissed || !user || user.email_verified !== false) return null;

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(t("auth.verify.resent"));
    } catch (err) {
      toast.error(t("auth.verify.resendError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="verify-email-banner" className="flex items-center gap-3 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 mb-6">
      <MailWarning size={18} className="text-warning shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{t("auth.verify.bannerTitle")}</p>
        <p className="text-xs text-muted-foreground truncate">{t("auth.verify.bannerBody", { email: user.email })}</p>
      </div>
      <button data-testid="verify-resend-btn" onClick={resend} disabled={sending}
        className="shrink-0 text-xs font-semibold text-primary hover:underline disabled:opacity-60">
        {sending ? t("common.loading") : t("auth.verify.resendCta")}
      </button>
      <button data-testid="verify-dismiss-btn" onClick={() => setDismissed(true)}
        className="shrink-0 grid place-items-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-warning/10">
        <X size={15} />
      </button>
    </div>
  );
}


export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [badges, setBadges] = useState({});
  const expertUnapproved = user?.role === "expert" && user?.expert_status && user.expert_status !== "approved";
  const items = expertUnapproved ? NAV.expertPending : (NAV[user?.role] || []);

  useEffect(() => {
    if (user?.role === "hr") {
      api.get("/company/needs").then(({ data }) => {
        setBadges((b) => ({ ...b, approvals: data.filter((n) => n.status === "pending").length }));
      }).catch(() => {});
      api.get("/company/requests").then(({ data }) => {
        setBadges((b) => ({ ...b, network: (data.incoming || []).length }));
      }).catch(() => {});
    }
    if (user?.role === "expert") {
      api.get("/expert/network-requests").then(({ data }) => {
        setBadges((b) => ({ ...b, networks: (data.incoming || []).length }));
      }).catch(() => {});
    }
  }, [user?.role]);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const contextName = user?.role === "hr" || user?.role === "employee"
    ? user?.company?.name || t("nav.workspace")
    : user?.role === "expert" ? t("nav.expertWorkspace") : t("nav.compeniPlatform");

  const SidebarInner = ({ onNavigate }) => (
    <div className="flex flex-col h-full">
      <div className="px-5 h-16 flex items-center border-b border-border">
        <Logo to={roleHome(user)} />
      </div>
      <div className="px-3 py-4 flex-1 overflow-y-auto">
        <NavItems items={items} badges={badges} onNavigate={onNavigate} />
      </div>
      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg bg-card border border-border">
          <span className="grid place-items-center w-8 h-8 rounded-md bg-primary/10 text-primary">
            {user?.role === "admin" ? <ShieldCheck size={16} /> : <Building2 size={16} />}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate text-foreground">{contextName}</p>
            <p className="text-[11px] text-muted-foreground capitalize">{t(ROLE_LABEL_KEY[user?.role])}</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-sidebar border-r border-border fixed inset-y-0 left-0 z-30">
        <SidebarInner />
      </aside>

      <div className="flex-1 lg:pl-64 min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-20 h-16 flex items-center gap-3 px-4 sm:px-6 bg-background/80 backdrop-blur-md border-b border-border">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu" className="lg:hidden grid place-items-center w-9 h-9 rounded-lg text-muted-foreground hover:bg-secondary">
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64 bg-sidebar">
              <SidebarInner onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="lg:hidden"><Logo showText={false} /></div>

          <div className="flex-1" />

          <LanguageSwitcher />
          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="flex items-center gap-2 pl-1 pr-1.5 py-1 rounded-lg hover:bg-secondary transition-colors">
                <UserAvatar src={user?.picture} name={user?.name} size={32} />
                <span className="hidden sm:block text-sm font-medium text-foreground max-w-32 truncate">{user?.name}</span>
                <ChevronsUpDown size={14} className="hidden sm:block text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="privacy-menu" onClick={() => navigate("/account/privacy")}>
                <ShieldCheck size={15} className="mr-2" /> {t("nav.privacyData")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-destructive focus:text-destructive">
                <LogOut size={15} className="mr-2" /> {t("common.signOut")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="px-4 sm:px-6 lg:px-10 py-8 max-w-[1400px] mx-auto"><VerifyEmailBanner />{children}</main>
      </div>
    </div>
  );
}
